import {
    CallerIdMessage,
    GetPrinterStatusPayload,
    PrinterCheckStatusPayload,
    PrinterSettings,
} from 'hooks/useHardwareAgent/Devices/types'
import isBase64 from 'is-base64'
import { useCallback, useMemo } from 'react'
import HttpRequest from 'httpRequest/HttpRequest'
import { convertSvgToPngAsync } from 'utilities/svgConverter'
export interface IPrinterConfig {
    name: string
    ipAddress?: string
    port?: number
    uniqueID?: string
    com?: string
    baudRate?: number
    data?: string
    pid?: number | null
    vid?: number | null
    isImage?: boolean
    cutting?: boolean
    printImageDelay?: number
    printerType: string
}
export interface IVersionModel {
    version: string
    informationalVersion: string
}
export interface UsbPrinter {
    deviceId: string
    deviceName: string
    manufacture: string
    productId: number
    vendorId: number
}

const useDeviceHardwareAgentCaller = (hwAgentUri?: string) => {
    const hardwareAgentCaller = useMemo(() => {
        if (!hwAgentUri) throw Error('No Hardware agent URI')
        return new HttpRequest(hwAgentUri)
    }, [hwAgentUri])

    const printRequest = useCallback(
        (data: IPrinterConfig, tag?: string) => {
            const url = tag ? '/printer/print?_tag=' + tag : '/printer/print'
            return hardwareAgentCaller.post(url, data)
        },
        [hardwareAgentCaller],
    )

    const openDrawerRequest = useCallback(
        (data: IPrinterConfig) => {
            return hardwareAgentCaller.post('/printer/open-drawer', data)
        },
        [hardwareAgentCaller],
    )

    const checkStatusRequest = useCallback(
        (payload: PrinterCheckStatusPayload) => {
            return hardwareAgentCaller.post('/printer/check-status', payload)
        },
        [hardwareAgentCaller],
    )

    const getPrinterStatusRequest = useCallback(
        (data: PrinterSettings) => {
            const payload: GetPrinterStatusPayload = {
                printer: data,
            }
            return hardwareAgentCaller.post('/printer/get-status', payload)
        },
        [hardwareAgentCaller],
    )

    const restartHardwareAgentRequest = useCallback(() => {
        return hardwareAgentCaller.post('/Restart')
    }, [hardwareAgentCaller])

    const getVersionRequest = useCallback(async () => {
        return await hardwareAgentCaller?.get<IVersionModel>('/system/version')
    }, [hardwareAgentCaller])

    const handlePrint = useCallback(
        async (
            config: IPrinterConfig,
            data: string,
            tag?: string,
        ): Promise<void> => {
            console.log(
                `printing something it gonna go fast from ${config.name}...`,
            )
            console.log('configuration = ', config)
            if (config.isImage) {
                // If data is already Base64, send to printer without manipulate
                if (isBase64(data)) {
                    await printRequest(
                        {
                            ...config,
                            data,
                        },
                        tag,
                    )
                } else {
                    // check `data` is image/svg+xml type, if yes converted to PNG
                    let newData =
                        data.indexOf('svg+xml') >= 0
                            ? await convertSvgToPngAsync(data)
                            : data

                    // strip only bytes base64
                    newData = newData.split(',')[1]

                    await printRequest(
                        {
                            ...config,
                            data: newData,
                        },
                        tag,
                    )
                }
            } else {
                const convertedData = isBase64(data) ? data : btoa(data)
                await printRequest(
                    {
                        ...config,
                        data: convertedData,
                    },
                    tag,
                )
            }
        },
        [printRequest],
    )

    const browseUsbDevicesRequest = useCallback(() => {
        return hardwareAgentCaller.get<UsbPrinter[]>(
            '/printer/list-usb-printers',
        )
    }, [hardwareAgentCaller])

    const handleCheckHWAgent = useCallback(async () => {
        await hardwareAgentCaller.get('/system')
    }, [hardwareAgentCaller])

    const hwAgentDownloadCertURI = useMemo(
        () => `${hwAgentUri}/system/download-root-ca`,
        [hwAgentUri],
    )
    const handleDownloadSSHCertificate = useCallback(async () => {
        const a = document.createElement('a')
        a.href = hwAgentDownloadCertURI
        a.target = '_blank'
        a.click()
        a.remove()
    }, [hwAgentDownloadCertURI])

    const getActiveCallerIdMessages = useCallback(async () => {
        return await hardwareAgentCaller.get<CallerIdMessage[]>('/callerid')
    }, [hardwareAgentCaller])

    const removeActiveCallerId = useCallback(
        async (phoneNumber: string) => {
            await hardwareAgentCaller.delete('/callerid/' + phoneNumber)
        },
        [hardwareAgentCaller],
    )

    return {
        handlePrint,
        openDrawerRequest,
        checkStatusRequest,
        getPrinterStatusRequest,
        restartHardwareAgentRequest,
        getVersionRequest,
        browseUsbDevicesRequest,
        handleDownloadSSHCertificate,
        handleCheckHWAgent,
        hwAgentDownloadCertURI,
        getActiveCallerIdMessages,
        removeActiveCallerId,
    }
}

export default useDeviceHardwareAgentCaller
